import { computed, inject, Injectable, signal } from '@angular/core';

import { EGoogleTagAnalyticsKeys } from '@/app/pages/sports/football/enums';
import { AuthService } from '@/app/services/auth/auth.service';

import { EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';
import { FsApiService } from '../auth/fs-api.service';
import { GoogleTagAnalyticsService } from '../google-tag';
import { LocalStorageService } from '../localstorage.service';
import { RoutingService } from '../routing.service';
import { FavoritesService } from './favorites.service';

import {
  IAllFavoritesCompetitions,
  IFavoritesCompetitions,
} from '@/app/models/favorite/competition-favorite.model';
import { pageSize } from '@/app/utils/constants';

const ID_LIST_COMPERTITION_FAVORITES = 'idListCompetitionFavorites';

export interface IBodyFCompetition {
  nameEs: string;
  nameEn: string;
  competitionLogo: string;
  country: {
    country: string;
    countryEn: string;
    countryEs: string;
    countryLogo: string;
    hasCountry: boolean;
  };
  fsIdCompetition: string;
}

@Injectable({
  providedIn: 'root',
})
export class CompetitionFavoritesService {
  favoritesService = inject(FavoritesService);

  private googleTagAnalyticsService = inject(GoogleTagAnalyticsService);
  private fsApi = inject(FsApiService);
  private routingService = inject(RoutingService);
  private lsService = inject(LocalStorageService);

  isLoadingCompetitionsFavorites = signal<boolean>(false);
  isErrorCompetitionsFavorites = signal<boolean>(false);
  competitionsFavorites = signal<IAllFavoritesCompetitions | null>(null);

  authService = inject(AuthService);

  isLoadingCompetitionFavorites = computed(() =>
    this.favoritesService.isLoadingCompetitionFavorites()
  );

  getAllFavoritesCompetition() {
    this.isLoadingCompetitionsFavorites.set(true);
    this.isErrorCompetitionsFavorites.set(false);

    const pageSize = 10;
    let currentPage = 1;

    this.fsApi
      .get<IAllFavoritesCompetitions>(`/competitions/favorites`, {
        params: { page: currentPage, size: pageSize },
      })
      .pipe(
        // Repite llamadas mientras haya más páginas
        expand((data) => {
          const res = data as IAllFavoritesCompetitions; // Forzamos el tipo
          if (!res || res.Page >= res.NumPages) {
            return EMPTY;
          }
          currentPage++;
          return this.fsApi.get<IAllFavoritesCompetitions>(
            `/competitions/favorites`,
            { params: { page: currentPage, size: pageSize } }
          ) as Observable<IAllFavoritesCompetitions>;
        }),
        // Acumula todos los resultados en un solo array
        reduce(
          (acc: IFavoritesCompetitions[], data: IAllFavoritesCompetitions) => {
            return [...acc, ...data.Competitions];
          },
          []
        ),
        // Manejo de errores
        catchError((err) => {
          console.error(err);
          this.isErrorCompetitionsFavorites.set(true);
          this.isLoadingCompetitionsFavorites.set(false);
          return of([]);
        }),
        // Transformar datos
        map((allCompetitions) => {
          return allCompetitions.map((competition) => {
            this.favoritesService.checkFavoriteCompetition(
              competition.Id,
              competition.IsFavorite
            );

            return {
              ...competition,
              Link: this.routingService.getTraslateUrlCompetitionDetails(
                competition.NameTranslation,
                competition.Name,
                competition.Id
              ),
            };
          });
        })
      )
      .subscribe((competitions) => {
        this.competitionsFavorites.set({
          Competitions: competitions,
          EndIndex: competitions.length - 1,
          NumPages: 1,
          Page: 1,
          PageSize: competitions.length,
          StartIndex: 0,
          Total: competitions.length,
        });
        this.isLoadingCompetitionsFavorites.set(false);
      });
  }

  getFavoritesCompetition(page?: number) {
    this.isLoadingCompetitionsFavorites.set(true);
    this.isErrorCompetitionsFavorites.set(false);

    this.fsApi
      .get<IAllFavoritesCompetitions>(`/competitions/favorites`, {
        params: { page: page ?? 1, size: pageSize },
      })
      .pipe(
        // Manejo de errores
        catchError((err) => {
          this.isErrorCompetitionsFavorites.set(true);
          this.isLoadingCompetitionsFavorites.set(false);
          return of([]);
        }),
        // Transformar datos
        map((allCompetitions) => {
          const favCompetitions = allCompetitions as IAllFavoritesCompetitions;

          return {
            ...favCompetitions,
            Competitions: favCompetitions.Competitions.map((competition) => {
              this.favoritesService.checkFavoriteCompetition(
                competition.Id,
                competition.IsFavorite
              );

              return {
                ...competition,
                Link: this.routingService.getTraslateUrlCompetitionDetails(
                  competition.NameTranslation,
                  competition.Name,
                  competition.Id
                ),
              };
            }),
          };
        })
      )
      .subscribe((competitions) => {
        if (
          this.competitionsFavorites() &&
          this.competitionsFavorites()!.Competitions.length > 0
        ) {
          this.competitionsFavorites.set({
            ...competitions,
            Competitions: [
              ...this.competitionsFavorites()?.Competitions!,
              ...competitions.Competitions,
            ],
          });
        } else {
          this.competitionsFavorites.set({
            ...competitions,
          });
        }

        this.isLoadingCompetitionsFavorites.set(false);
      });
  }

  setCompetitionFavorites(id: string, body: IFavoritesCompetitions) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.postFavoritesCompetition(id);

    this.competitionsFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Competitions: [body, ...(prev?.Competitions ?? [])],
    }));

    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.ADD_COMPETITION_FAVORITE,
      {
        ...body,
        competitionId: id,
        isFavorite: true,
      }
    );
  }

  deleteCompetitionFavorites(id: string) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.deleteFavoritesCompetition(id);

    this.competitionsFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Competitions:
        prev?.Competitions.filter((competition) => competition.Id !== id) ?? [],
    }));

    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.REMOVE_COMPETITION_FAVORITE,
      {
        idCompetition: id,
        // ...body,
        isFavorite: false,
      }
    );
  }

  addCompetitonServiceTest(id: string) {
    this.favoritesService.idsCompetitionFavorites.update((prev) => [
      ...prev,
      id,
    ]);
    localStorage.setItem(
      ID_LIST_COMPERTITION_FAVORITES,
      JSON.stringify(this.favoritesService.idsCompetitionFavorites())
    );
  }
  deleteCompetitonServiceTest(id: string) {
    this.favoritesService.idsCompetitionFavorites.update((prev) =>
      prev.filter((competitionId) => competitionId !== id)
    );
    localStorage.setItem(
      ID_LIST_COMPERTITION_FAVORITES,
      JSON.stringify(this.favoritesService.idsCompetitionFavorites())
    );
  }

  isFavoriteCompetition(id: string) {
    return this.favoritesService.idsCompetitionFavorites().includes(id);
  }
}
